import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, batch } from "react-redux";
import { get } from "lodash";
import { getBetType, getBetLeg } from "@tvg/desktop-bet/src/store/selectors";
import {
  resetBetSelection,
  resetBetSelectionsKeepWin,
  setBetType,
  setPickLeg,
  setBetAmount,
  setRaceOfBet
} from "@tvg/desktop-bet/src/store/actions";
import { RaceStatusEnum } from "@tvg/ts-types/Race";
import {
  BetTypeSelector as BetTypeSelectorDs,
  useTheme,
  MtpCell,
  AlertInline,
  useMediaQuery,
  breakpoints
} from "@tvg/design-system";
import { WagerType, WagerTypesGroup } from "@tvg/ts-types/Wager";
import { usePrevious } from "@tvg/custom-hooks";
import { useLocation } from "react-router";
import { getOnlyAvailableBetTypes } from "@tvg/desktop-bet/src/utils/betUtils";
import { isMobileServerSide } from "@tvg/sh-utils/mobileUtils";
import {
  Wrapper,
  TrackSummaryInfo,
  RaceNotOpenWrapper,
  TrackSummaryAnimationSlider,
  RaceNotOpenAlertWrapper
} from "./styled-components";
import BetTypeSelectorMobile from "./BetTypeSelectorMobile";
import { BetTypeListItem } from "./BetTypeSelectorMobile/types";
import {
  getProgramPageMessages,
  getBlacklistedWagerTypes
} from "../../../store/selectors";
import { betSelectorGtmEvent } from "../../../utils/gtm/navigation";
import BetSelectorLoading from "../../loadings/BetSelectorLoading";
import { isRaceoff } from "../../../utils/raceUtils";
import {
  RaceStatusAnimationPayload,
  BetTypeSelectorProps,
  TrackSummaryInfoContentProps
} from "./types";

const TrackSummaryInfoContent = ({
  mtp,
  status,
  trackCode,
  raceNumber,
  postTime,
  raceStatusAnimation
}: TrackSummaryInfoContentProps) => (
  <>
    <MtpCell
      mtp={+mtp}
      status={status}
      showMtp
      trackCode={trackCode || ""}
      raceNumber={raceNumber || ""}
      postTime={postTime as string}
      withBackground
    />
    <TrackSummaryAnimationSlider
      data-qa-label="track-summary-info-animation-slider"
      raceStatusAnimation={raceStatusAnimation}
      position="absolute"
    />
  </>
);

const BetTypeSelector: React.FC<BetTypeSelectorProps> = ({
  currentRace,
  isLoading,
  isMtpHidden
}) => {
  const theme = useTheme();
  const isMobile =
    useMediaQuery(breakpoints.tablet.max.sm) || isMobileServerSide();
  const [betTypesList, setBetTypesList] = useState<WagerType[]>([]);
  const [mobileBetTypesList, setMobileBetTypesList] = useState<
    BetTypeListItem[]
  >([]);
  const [raceStatusAnimation, setRaceStatusAnimation] =
    useState<null | RaceStatusAnimationPayload>(null);
  const [isBetTypesLoading, setIsBetTypesLoading] = useState(true);
  const selectedBetType = useSelector(getBetType);
  const selectedBetLeg = useSelector(getBetLeg);
  const blacklistedBets = useSelector(getBlacklistedWagerTypes);
  const programPageMessages = useSelector(getProgramPageMessages);
  const location = useLocation();
  const dispatch = useDispatch();
  const searchRaceNumber = location.search;
  let raceNumber: string | null;
  const trackName = location.pathname.split("/")[3];
  // MTP has two formats: hour (02:50am) or "x minutes till post"
  // "longerMtp" represents the "hour format"
  const longerMtp = (currentRace?.mtp || 0) > 59;
  const mtp = get(currentRace, "mtp", "0");
  const postTime = get(currentRace, "postTime");
  const status = get(currentRace, "status.code");
  const previousMtp = usePrevious(mtp);
  const previousStatus = usePrevious(status);
  const raceStatusAnimationPayload = {
    fiveMtp: {
      backgroundColor: theme.colors.red[500],
      animationName: "fiveMtp"
    },
    raceOff: {
      backgroundColor: theme.colors.green[500],
      animationName: "raceOff"
    },
    resulted: {
      backgroundColor: theme.colors.blue[500],
      animationName: "resulted"
    }
  };

  if (typeof searchRaceNumber === "string") {
    raceNumber = searchRaceNumber.split("=")[1];
  }

  useEffect(() => {
    if (currentRace.wagerTypes) {
      const availableBetTypes = getOnlyAvailableBetTypes(
        currentRace.wagerTypes?.map((wager) => wager.type),
        blacklistedBets
      );
      setBetTypesList(availableBetTypes);

      const mobileBetTypes = availableBetTypes.reduce(
        (acc: BetTypeListItem[], current: WagerType) => {
          const betType = currentRace.wagerTypes?.find(
            (wagerType) => wagerType.type.id === current.id
          );
          if (betType) {
            acc.push({
              ...betType.type,
              legCount: betType.legCount
            });
          }
          return acc;
        },
        []
      );
      setMobileBetTypesList(mobileBetTypes);
    }

    setIsBetTypesLoading(!currentRace.id && !currentRace.wagerTypes);
  }, [JSON.stringify(blacklistedBets), currentRace]);

  // Reset the state to prevent the animation to be triggered again
  // once the user shows and hides the MTP again
  useEffect(() => {
    setRaceStatusAnimation(null);
  }, [isMtpHidden]);

  useEffect(() => {
    const { RACE_OFF, RACE_OFFICIAL } = RaceStatusEnum;
    if (previousMtp === 6 && mtp === 5) {
      setRaceStatusAnimation(raceStatusAnimationPayload.fiveMtp);
    } else if (previousMtp === 0 && status === RACE_OFF) {
      setRaceStatusAnimation(raceStatusAnimationPayload.raceOff);
    } else if (previousStatus === RACE_OFF && status === RACE_OFFICIAL) {
      setRaceStatusAnimation(raceStatusAnimationPayload.resulted);
    }
  }, [mtp, status]);

  const onSelectBetType = useCallback(
    (id: number) => {
      batch(() => {
        dispatch(setBetType(id));
        dispatch(setRaceOfBet(currentRace.id || ""));
      });

      const wagerSelected = betTypesList.find(
        (wagerType) => wagerType.id === id
      );

      if (selectedBetType !== id) {
        if (
          WagerTypesGroup.WIN.includes(selectedBetType) &&
          WagerTypesGroup.WIN.includes(id)
        ) {
          let columns: number | undefined;
          currentRace.wagerTypes?.forEach((wt) => {
            if (wt.type.id === id) {
              columns = wt.columnCount;
            }
          });
          dispatch(resetBetSelectionsKeepWin(columns));
        } else {
          dispatch(setPickLeg(0));
          dispatch(resetBetSelection());
          requestAnimationFrame(() => {
            dispatch(setBetAmount(0));
          });
        }

        betSelectorGtmEvent({
          betType: wagerSelected?.code,
          raceNumber,
          trackName
        });
      }
    },
    [betTypesList]
  );

  const onSelectBetLeg = (leg: number) => {
    dispatch(setPickLeg(leg));
  };

  if (isLoading || isBetTypesLoading) {
    return <BetSelectorLoading isMobile={isMobile} />;
  }

  const isResulted = !!(
    currentRace.results?.runners || currentRace.results?.payoffs
  );

  if (isRaceoff(currentRace)) {
    return (
      <RaceNotOpenWrapper theme={theme}>
        {isMtpHidden && (
          <TrackSummaryInfoContent
            mtp={+mtp}
            status={RaceStatusEnum.RACE_OFF}
            trackCode={currentRace?.track?.trackCode || ""}
            raceNumber={currentRace.raceNumber || ""}
            postTime={postTime as string}
            raceStatusAnimation={raceStatusAnimation}
          />
        )}
        <RaceNotOpenAlertWrapper>
          <AlertInline
            message={get(
              programPageMessages,
              "raceoff.betTypeAlertMessage",
              ""
            )}
            qaLabel="bet-type-selector-raceoff-message"
            variant="informational"
            type="fixed"
            paddingY={isMobile ? 0 : "space-6"}
          />
        </RaceNotOpenAlertWrapper>
      </RaceNotOpenWrapper>
    );
  }

  if (status === RaceStatusEnum.RACE_OFFICIAL) {
    if (isMtpHidden && !isResulted) {
      return (
        <RaceNotOpenWrapper theme={theme}>
          <TrackSummaryInfoContent
            mtp={+mtp}
            status={RaceStatusEnum.RACE_OFFICIAL}
            trackCode={currentRace?.track?.trackCode || ""}
            raceNumber={currentRace.raceNumber || ""}
            postTime={postTime as string}
            raceStatusAnimation={raceStatusAnimation}
          />
        </RaceNotOpenWrapper>
      );
    }
    return null;
  }

  if (betTypesList.length === 0) {
    return (
      <AlertInline
        message={get(
          programPageMessages,
          "betList.emptyBetTypeListMessage",
          ""
        )}
        qaLabel="bet-type-selector-raceoff-message"
        variant="informational"
        type="fixed"
        paddingY={isMobile ? 0 : "space-6"}
      />
    );
  }

  if (isMobile) {
    return (
      <BetTypeSelectorMobile
        key={`bet-type-selector-${currentRace.id}-${currentRace.raceNumber}`}
        betTypesList={mobileBetTypesList}
        selectedBetType={selectedBetType}
        selectedBetLeg={selectedBetLeg}
        currentRaceId={currentRace.id}
        onSelectBetType={onSelectBetType}
        onSelectBetLeg={onSelectBetLeg}
      />
    );
  }

  return (
    <Wrapper theme={theme}>
      {isMtpHidden && (
        <TrackSummaryInfo
          data-qa-label="track-summary-info"
          position="absolute"
          theme={theme}
          longerMtp={longerMtp}
        >
          <TrackSummaryInfoContent
            mtp={+mtp}
            status={status}
            trackCode={currentRace?.track?.trackCode || ""}
            raceNumber={currentRace.raceNumber || ""}
            postTime={postTime as string}
            raceStatusAnimation={raceStatusAnimation}
          />
        </TrackSummaryInfo>
      )}
      <BetTypeSelectorDs
        betTypesList={betTypesList}
        qaLabel="bet-type-selector"
        selectedBetType={selectedBetType}
        setSelectedBetType={onSelectBetType}
      />
    </Wrapper>
  );
};

export default BetTypeSelector;
